import { useEffect, useRef, useState } from 'react';

/**
 * The one timer behind the People screen's search box.
 *
 * It is a plain factory rather than a hook body so it can be *asserted*: the
 * SPA's suite has no DOM, and a `useEffect` + `setTimeout` written inline is
 * exactly the kind of thing that is only ever exercised by hand. What matters
 * about a search box is that typing eight characters asks the server once, for
 * the eighth — and that is a statement about this function, not about React.
 *
 * `push` restarts the wait. `cancel` drops a pending value on the floor, which
 * is what unmounting must do: a settle firing into a component that is gone is a
 * state set nobody is watching.
 */
export interface Debouncer<T> {
  push(value: T): void;
  cancel(): void;
}

export function debouncer<T>(ms: number, emit: (value: T) => void): Debouncer<T> {
  let timer: ReturnType<typeof setTimeout> | null = null;

  const cancel = () => {
    if (timer !== null) clearTimeout(timer);
    timer = null;
  };

  return {
    push(value) {
      cancel();
      timer = setTimeout(() => {
        timer = null;
        emit(value);
      }, ms);
    },
    cancel,
  };
}

/** How long the search box waits before it asks. */
export const SEARCH_DEBOUNCE_MS = 250;

/**
 * A value that lags what is being typed. The caller holds the live text (the
 * input is controlled by it, so every keystroke shows immediately) and reads the
 * settled one, which is what the read is keyed on.
 */
export function useSettled<T>(initial: T, ms: number): [T, (next: T) => void] {
  const [settled, setSettled] = useState(initial);
  const debounce = useRef<Debouncer<T> | null>(null);
  if (debounce.current === null) debounce.current = debouncer<T>(ms, setSettled);

  useEffect(() => {
    const current = debounce.current;
    return () => current?.cancel();
  }, []);

  return [settled, (next: T) => debounce.current?.push(next)];
}
